import React, { useEffect, useImperativeHandle, useRef } from 'react';
import { TourProvider, useTour, StepType } from '@reactour/tour';

import style from './index.less';

interface RefHandles {
  refresh(): void;
}

interface TourProps {
  handleStep?: (currentStep: number) => void;
  handleClose?: (event: any) => void;
  current: number;
  stepsSetting: Array<StepType>;
  isOpen: boolean;
}

interface NoviceTourPorps {
  tourProps: TourProps;
}

// 内部同步组件：将外部受控状态同步到 @reactour/tour 内部状态
const TourController = ({
  isOpen,
  current,
  handleStep,
  handleClose,
  innerRef,
}: {
  isOpen: boolean;
  current: number;
  handleStep?: (step: number) => void;
  handleClose?: (event: any) => void;
  innerRef: React.Ref<RefHandles>;
}) => {
  const { setIsOpen, setCurrentStep, currentStep } = useTour();
  const prevExternalCurrent = useRef(current);

  useEffect(() => {
    setIsOpen(isOpen);
  }, [isOpen]);

  useEffect(() => {
    prevExternalCurrent.current = current;
    setCurrentStep(current);
  }, [current]);

  // 内部步骤变化时通知外部（避免循环：只在非外部触发的变化时通知）
  useEffect(() => {
    if (currentStep !== prevExternalCurrent.current) {
      handleStep && handleStep(currentStep);
    }
  }, [currentStep]);

  useImperativeHandle(innerRef, () => ({
    refresh: () => {
      // 强制重新定位，通过微小的步骤切换触发重渲染
      setCurrentStep(c => {
        const next = c;
        return next;
      });
    }
  }));

  return null;
};

const NoviceTour = React.forwardRef<RefHandles, NoviceTourPorps>((props, cRef) => {
  const { tourProps } = props;
  const { handleStep, handleClose, current, stepsSetting, isOpen } = tourProps;

  const onClickClose = ({ setIsOpen }: { setIsOpen: React.Dispatch<React.SetStateAction<boolean>> }) => {
    setIsOpen(false);
    if (handleClose) handleClose(null);
  };

  const onClickMask = ({ setIsOpen }: { setIsOpen: React.Dispatch<React.SetStateAction<boolean>> }) => {
    setIsOpen(false);
    if (handleClose) handleClose(null);
  };

  return (
    <TourProvider
      steps={stepsSetting}
      defaultOpen={false}
      showNavigation={false}
      showBadge={false}
      showPrevNextButtons={false}
      showCloseButton={true}
      className={style.tour}
      maskClassName={style.mask}
      highlightedMaskClassName="noviceHighlighted"
      onClickClose={onClickClose}
      onClickMask={onClickMask}
      styles={{
        popover: base => ({ ...base, borderRadius: 4 })
      }}
    >
      <TourController
        isOpen={isOpen}
        current={current}
        handleStep={handleStep}
        handleClose={handleClose}
        innerRef={cRef}
      />
    </TourProvider>
  );
});

export default NoviceTour;
